import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAllSearchParams } from 'storfox-route-hooks'
import { prop } from 'ramda'
import { useDeepCompareEffect } from 'storfox-api-hooks'
import { useSnackbar } from 'storfox-snackbar'

import { escapeAtob, unescapeBtoa } from '~/utils'
import { DashboardLayout } from '~/components/Layouts'
import { useNotification } from '~/components/Notification'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import { CITY_MAPPING_BULK_VALIDATE_COMPLETE, CITY_MAPPING_BULK_VALIDATE_STATUS } from '~/constants/notification-topics'
import { useProgressCard } from '~/components/ProgressCard'
import { useGoogleEvent } from '~/components/GoogleAnalytics/GoogleAnalytics'
import useMessages from '~/hooks/useMessages'

import { useCityMappingImport } from '../hooks'
import CityMappingImport from '../components/CityMappingImport/CityMappingImport'

function CityMappingImportContainer () {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const cityMappingImport = useCityMappingImport()
  const { payload } = useAllSearchParams()
  const { sendEvent } = useGoogleEvent()
  const progressCard = useProgressCard()
  const [submitted, setSubmitted] = useState(false)

  const validateStatus = useNotification(CITY_MAPPING_BULK_VALIDATE_STATUS)
  const validateComplete = useNotification(CITY_MAPPING_BULK_VALIDATE_COMPLETE)

  const key = prop('key', escapeAtob(payload))

  useDeepCompareEffect(() => {
    if (key) {
      validateStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const validated = prop('totalValidated', result)
        const total = prop('total', result)

        progressCard.handleValidationChange({ current: validated, total })
      })

      validateComplete.subscribe(key, ({ payload }) => {
        navigate(
          `${ROUTES.CITY_MAPPING_IMPORT_PATH}?payload=${unescapeBtoa(payload)}`
        )

        validateComplete.setLoading(false)
      }, { replace: true })
    }

    return () => {
      validateStatus.unsubscribe()
      validateComplete.unsubscribe()
    }
  }, [key])

  const handleSubmit = () => {
    return cityMappingImport.create({ key })
      .then(() => snackbar({ message: messages.IMPORT_REQUEST_SUCCESS }))
      .then(() => setSubmitted(true))
  }

  const handleSubscribe = key => {
    snackbar({ message: messages.NOTIFICATION_WAIT })
    validateComplete.setLoading(true)
    navigate(`${ROUTES.CITY_MAPPING_IMPORT_PATH}?payload=${unescapeBtoa({ key })}`, { replace: true })
    sendEvent({ eventAction: 'Validate File City Mapping', eventCategory: 'City Mapping' })
  }

  const handleFileRemove = () => {
    validateComplete.unsubscribe()
    validateStatus.unsubscribe()
    progressCard.resetValues()
  }

  const title = 'Import City Mapping'

  const isLoading = validateComplete.isLoading

  const breadcrumbs = { title }

  return (
    <DashboardLayout
      title={title}
      activeNav={NAV.CITY_MAPPING}
      breadcrumbs={breadcrumbs}
    >
      <CityMappingImport
        title={title}
        isLoading={isLoading}
        progressCard={progressCard}
        onSubscribe={handleSubscribe}
        onSubmit={handleSubmit}
        onFileRemove={handleFileRemove}
        submitted={submitted}
      />
    </DashboardLayout>
  )
}

export default CityMappingImportContainer
